import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Copy, Eye, Send, AlertTriangle, ShieldCheck, Rocket, Loader2 } from "lucide-react";
import { TemplateEditor } from "@/components/rehab/TemplateEditor";
import { TemplatePreview } from "@/components/rehab/TemplatePreview";
import { CommercialPanel } from "@/components/rehab/CommercialPanel";
import { CapabilityPanel } from "@/components/rehab/CapabilityPanel";

const STATUS_CLS = { draft: "text-zinc-400", changes_requested: "text-amber-400", submitted: "text-sky-400", approved: "text-emerald-400", active: "text-emerald-300", superseded: "text-zinc-600", withdrawn: "text-zinc-500" };

export default function RehabTemplates() {
  const { user } = useAuth();
  const isAdminArea = useLocation().pathname.startsWith("/admin");
  const Layout = isAdminArea ? AdminLayout : DashLayout;
  const [list, setList] = useState([]);
  const [dueOnly, setDueOnly] = useState(false);
  const [sel, setSel] = useState(null);
  const [ver, setVer] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/rehab-templates", { params: { due_only: dueOnly } }).then((r) => setList(r.data.templates || []));
  const open = async (id) => {
    const r = await api.get(`/rehab-templates/${id}`);
    setSel(r.data); setVer(r.data.versions[0] || null); setPreview(null);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [dueOnly]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await refresh(); if (sel) await open(sel.id); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || JSON.stringify(d) || "Failed"); }
    finally { setBusy(false); }
  };

  const create = () => act(async () => {
    const r = await api.post("/rehab-templates", { title: "New programme template" });
    await open(r.data.id);   // fetch enriched detail (can_manage/review/publish) so the editor is writable
  }, "Draft created");
  const saveDraft = (content) => act(() => api.put(`/rehab-templates/${sel.id}/versions/${ver.id}`, content), "Draft saved");
  const editable = ver && ["draft", "changes_requested"].includes(ver.status);
  const isAuthor = ver && user && ver.created_by === user.id;
  const canPublishNow = sel?.can_publish && ver && ["approved", "active"].includes(ver.status) && !(sel.missing_commercial || []).length && sel.active_version_id !== ver.id;

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="rehab-templates-page">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-white">Programme Roadmaps</h1>
          <span className="text-xs text-zinc-500">Reusable · not linked to patients · inactive for sale until approved and activated</span>
          <div className="ml-auto flex gap-2">
            <label className="text-xs text-zinc-400 flex items-center gap-1"><input type="checkbox" data-testid="due-only-toggle" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} /> Due for review</label>
            {!isAdminArea && <button className="fl-btn text-xs" data-testid="new-template-btn" onClick={create} disabled={busy}><Plus size={14} /> New programme</button>}
          </div>
        </div>

        {isAdminArea && user?.role === "admin" && <CapabilityPanel />}

        <div className="grid lg:grid-cols-[320px_1fr] gap-4">
          <div className="space-y-1" data-testid="template-list">
            {list.map((t) => (
              <button key={t.id} data-testid={`template-row-${t.slug || t.id}`} onClick={() => open(t.id)}
                className={`w-full text-left rounded-lg border px-3 py-2 ${sel?.id === t.id ? "border-orange-500/60 bg-zinc-900" : "border-zinc-800 hover:border-zinc-600"}`}>
                <div className="flex items-center gap-2 text-sm text-zinc-100">{t.title}
                  {t.due_for_review && <AlertTriangle size={12} className="text-amber-400" data-testid="due-indicator" />}</div>
                <div className="text-[11px] text-zinc-500 flex gap-2 flex-wrap">
                  <span className={STATUS_CLS[t.latest_version?.status]}>v{t.latest_version?.number} {t.latest_version?.status}</span>
                  <span>· sale: {t.sale_status}</span>{t.pathway_id && <span>· {t.pathway_id}</span>}
                  {t.derived_from_template_id && <span className="text-zinc-600">· copy</span>}
                </div>
                {t.pathway_note && <div className="text-[11px] text-amber-500 mt-1">{t.pathway_note}</div>}
              </button>
            ))}
            {!list.length && <div className="text-sm text-zinc-500">No programmes{dueOnly ? " due for review" : ""}.</div>}
          </div>

          {sel && ver && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <select className="fl-input text-xs" data-testid="version-select" value={ver.id} onChange={(e) => { setVer(sel.versions.find((v) => v.id === e.target.value)); setPreview(null); }}>
                  {sel.versions.map((v) => { const label = `v${v.number} · ${v.status}${v.id === sel.active_version_id ? " · ON SALE" : ""}`; return <option key={v.id} value={v.id}>{label}</option>; })}
                </select>
                <button className="fl-btn-ghost text-xs" data-testid="preview-btn" onClick={() => api.get(`/rehab-templates/${sel.id}/versions/${ver.id}/preview`).then((r) => setPreview(r.data))}><Eye size={14} /> Preview</button>
                {sel.can_manage && <>
                  <button className="fl-btn-ghost text-xs" data-testid="duplicate-btn" onClick={() => act(() => api.post(`/rehab-templates/${sel.id}/duplicate`), "Duplicated into a new programme")}><Copy size={14} /> Duplicate</button>
                  {!editable && <button className="fl-btn-ghost text-xs" data-testid="new-draft-btn" onClick={() => act(() => api.post(`/rehab-templates/${sel.id}/versions/${ver.id}/new-draft`), "New draft created")}>New draft from v{ver.number}</button>}
                  {editable && <button className="fl-btn text-xs" data-testid="submit-review-btn" onClick={() => act(() => api.post(`/rehab-templates/${sel.id}/versions/${ver.id}/submit`), "Submitted for clinical review")}><Send size={14} /> Submit for review</button>}
                </>}
                {ver.status === "submitted" && sel.can_review && !isAuthor && <>
                  <button className="fl-btn text-xs" data-testid="approve-btn" onClick={() => act(() => api.post(`/rehab-templates/${sel.id}/versions/${ver.id}/approve`, { notes: window.prompt("Review notes") || null }), "Version approved")}><ShieldCheck size={14} /> Approve</button>
                  <button className="fl-btn-ghost text-xs" data-testid="request-changes-btn" onClick={() => act(() => api.post(`/rehab-templates/${sel.id}/versions/${ver.id}/request-changes`, { notes: window.prompt("What needs changing?") || null }), "Changes requested")}>Request changes</button>
                </>}
                {ver.status === "submitted" && isAuthor && <span className="text-[11px] text-amber-500">You authored this — a different reviewer must approve.</span>}
                {sel.can_publish && canPublishNow && <button className="fl-btn text-xs" data-testid="publish-btn" onClick={() => act(() => api.post(`/admin/rehab-templates/${sel.id}/activate`, { version_id: ver.id }), "Activated for sale")}><Rocket size={14} /> Publish v{ver.number}</button>}
                {sel.can_publish && sel.sale_status === "active" && <button className="fl-btn-ghost text-xs" data-testid="withdraw-toolbar-btn" onClick={() => act(() => api.post(`/admin/rehab-templates/${sel.id}/withdraw`), "Withdrawn from sale")}>Withdraw</button>}
                {busy && <Loader2 size={14} className="animate-spin text-zinc-500" />}
              </div>

              {ver.approval && <div className="text-xs text-emerald-400" data-testid="approval-record">Approved by {ver.approval.reviewer_name} on {ver.approval.at?.slice(0, 10)}{ver.approval.notes ? ` — ${ver.approval.notes}` : ""}</div>}
              {sel.can_publish && (sel.missing_commercial || []).length > 0 && ["approved", "active"].includes(ver.status) && <div className="text-[11px] text-amber-400">Commercial settings incomplete: {sel.missing_commercial.join(", ")} — an admin must set these before publishing.</div>}

              {isAdminArea && user?.role === "admin" && <CommercialPanel template={sel} version={ver} onChange={() => open(sel.id)} />}

              {preview ? <TemplatePreview data={preview} onClose={() => setPreview(null)} />
                : <TemplateEditor key={ver.id} version={ver} readOnly={!editable || !sel.can_manage} onSave={saveDraft} />}

              <details className="text-xs text-zinc-500"><summary>Change & approval history</summary>
                <ul className="mt-1 space-y-0.5" data-testid="version-history">
                  {ver.change_summary && <li>Change summary: {ver.change_summary}</li>}
                  {ver.based_on && <li>Based on an earlier version</li>}
                  {(ver.history || []).map((h, i) => <li key={i}>{h.at?.slice(0, 16)} · {h.event} · {h.by}{h.notes ? ` — ${h.notes}` : ""}</li>)}
                </ul></details>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
